import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { LoadingOverlay } from "@/components/ui/loading-spinner";
import { SuperAdminDashboard } from "./dashboards/SuperAdminDashboard";
import { TenantAdminDashboard } from "./dashboards/TenantAdminDashboard";
import { LibrarianDashboard } from "./dashboards/LibrarianDashboard";
import { PatronDashboard } from "./dashboards/PatronDashboard";
import { StaffDashboard } from "./dashboards/StaffDashboard";
import { UserRole, isGlobalSuperAdmin } from "@/lib/permissions";

const DashboardPage = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Restore last location on mount if available (only on actual refresh, not navigation)
  useEffect(() => {
    if (isAuthenticated && !isLoading && location.pathname === "/dashboard") {
      const lastLocation = sessionStorage.getItem("lastLocation");
      // Only restore if it's a valid authenticated route (not public routes)
      const publicRoutes = [
        "/login",
        "/",
        "/onboard",
        "/register",
        "/verify-email",
        "/reset-password",
      ];
      if (
        lastLocation &&
        lastLocation !== "/dashboard" &&
        !publicRoutes.some((route) => lastLocation.startsWith(route))
      ) {
        navigate(lastLocation, { replace: true });
        sessionStorage.removeItem("lastLocation");
      }
    }
  }, [isAuthenticated, isLoading, navigate, location.pathname]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingOverlay size="xl" label="Loading your dashboard..." />
      </div>
    );
  }

  // Show error if not authenticated
  if (!isAuthenticated || !user) {
    return null; // Will redirect
  }

  // Determine user role
  const getUserRole = (): UserRole | undefined => {
    // Super admin check
    if (isGlobalSuperAdmin(user)) return "SUPER_ADMIN";

    // Staff members authenticated via staff login
    // They have isStaff=true and their role comes from the staff record, not memberships
    if (user.isStaff) {
      const staffRoleName = user.roleName?.toUpperCase()?.replace(/\s+/g, "_");

      // Map staff role names to UserRole for dashboard routing
      if (staffRoleName?.includes("TENANT") && staffRoleName?.includes("ADMIN"))
        return "TENANT_ADMIN";
      if (staffRoleName === "LIBRARIAN") return "LIBRARIAN";

      // All other staff roles (Circulation Desk, Cataloger, Acquisitions Staff,
      // Reference Librarian, custom roles) use the generic staff dashboard
      // which is permission-aware
      return "STAFF";
    }

    // Regular user - check memberships
    const activeMembership = user.memberships?.[0];
    if (!activeMembership) {
      console.warn("User has no memberships", user);
      return undefined;
    }

    // Check roleName first (from Role.name), then fall back to role (enum)
    const roleName =
      activeMembership.roleName?.toUpperCase() || activeMembership.role?.toUpperCase();

    // Handle various formats: "TENANT ADMIN", "TENANT_ADMIN", etc.
    if (roleName?.includes("TENANT") && roleName?.includes("ADMIN")) return "TENANT_ADMIN";
    if (roleName === "LIBRARIAN") return "LIBRARIAN";
    if (roleName === "STUDENT" || roleName === "PATRON") return "PATRON";

    console.warn("Unknown role:", { roleName, activeMembership });
    return undefined;
  };

  const userRole = getUserRole();

  // Render appropriate dashboard based on role
  const renderDashboard = () => {
    switch (userRole) {
      case "SUPER_ADMIN":
        return <SuperAdminDashboard />;
      case "TENANT_ADMIN":
        return <TenantAdminDashboard />;
      case "LIBRARIAN":
        // Librarian who logged in as staff uses the permission-aware staff dashboard
        // Librarian who logged in as regular user uses the simplified librarian dashboard
        return user.isStaff ? <StaffDashboard /> : <LibrarianDashboard />;
      case "STAFF":
        return <StaffDashboard />;
      case "PATRON":
      case "STUDENT":
        return <PatronDashboard />;
      default:
        return (
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">Your account does not have a valid role assigned.</p>
            <p className="text-sm text-gray-500 mt-2">Please contact your administrator.</p>
          </div>
        );
    }
  };

  return <AppLayout>{renderDashboard()}</AppLayout>;
};

export default DashboardPage;
